/**
 * EventItem — hydrated wrappers over a canonical `Event` that
 * expose typed time ranges and update helpers.
 *
 * Port of lona-js's `event-item.ts`, narrowed to the surface
 * EventsIndex actually consumes:
 *   - `EventItemTimed` (st/et → `DateTime.Range<Utc>`) for
 *     timed events. `makeRecurrenceGenerator` parses the RRULE.
 *   - `EventItemAllDay` (startDate/endDate → `NaiveDate.Range`)
 *     for all-day events.
 *
 * Dropped from the upstream port:
 *   - `rowProps`, `acid`/`acal`, `isEditable(calendars)`,
 *     `backgroundColor`, `categoryItem`, `firstMeetingLink` —
 *     UI/calendar-metadata concerns that belong in the app
 *     layer (lona-js / @tento/lona/sheets-ui).
 *   - SheetsNetwork.EventCell — replaced with the local `Event` shape from
 *     `dtype.ts`. A change to an event is an `EventPatch`
 *     (`./event-patch.ts`), which paints through the update helpers below.
 */

import type { AuthId, CalendarId, EventId } from "../../ids/ids.ts";
import type { Option } from "../../core/option.ts";
import {
  DateFragment,
  DateTime,
  DateTimeRegion,
  Duration,
  GoogleEventGenerator,
  IsoDate,
  NaiveDate,
  NaiveTime,
  Recurrence,
  RecurrenceComposition,
  TimeOfDay,
  TimezoneRegion,
  Tzname,
  Utc,
} from "@tento-chrono";
import { nonnull } from "@tento-core/assert";
import { Tracing } from "@tento-core/tracing";

import type { Attendee } from "../../core/dtype.ts";
import { CalendarEvent } from "../../core/dtype.ts";
import type { EventOrigin } from "../../tx/event-origin.ts";
import { RecurrenceInfo } from "./recurrence-info.ts";
import { ZoneLoad } from "../../core/zone-load.ts";
import { TimeMembership } from "../../render/time-membership.ts";

/** `event` with its extra `key` set to `value` (`null`: the event has none). */
function withField<K extends CalendarEvent.Extra>(
  event: CalendarEvent,
  key: K,
  value: CalendarEvent[K],
): CalendarEvent {
  const next: CalendarEvent = { ...event };
  next[key] = value;
  return next;
}

/** `attendee` answering `status`, or with no answer when `status` is `null`. */
function withResponseStatus(attendee: Attendee, status: Option<string>): Attendee {
  return { ...attendee, responseStatus: status };
}

export { RecurrenceInfo };
export type { Attendee };

/** Numeric interval used as an interval-tree key. Mirrors lona-js's `NumRangeLike`. */
export type NumRangeLike = { start: number; end: number };


/** A single conference entry point parsed out of `Event.conferenceData`
 *  (Google's `conferenceData.entryPoints[]`). */
type EntryPoint = {
  entryPointType: Option<string>;
  label: Option<string>;
  uri: Option<string>;
};

type ConferenceData = {
  entryPoints: Option<readonly EntryPoint[]>;
};

// ── Internal base ──────────────────────────────────────────────────

abstract class EventItemBase {
  constructor(
    readonly inner: CalendarEvent,
    /** `RecurrenceInfo.ofEvent(inner)` for the info the event implies. */
    readonly recurrenceInfo: Option<RecurrenceInfo>,
    /**
     * Opaque origin of this event — where it came from (its data-owning
     * source row). Stamped by the index/provider that loaded it, and
     * used to route mutations back to the writable source.
     */
    readonly origin: EventOrigin,
  ) {}

  get parentId(): EventId {
    return this.inner.id;
  }

  get id(): EventId {
    return this.recurrenceInfo?.id ?? this.inner.id;
  }

  /**
   * Auth-scoped calendar id `[authId, calendarId]` — the event's identity
   * on its provider. Pure `Event` data; the typed `AuthScopedCalendarId`
   * alias lives in the SDK layer (`@tento/lona`), but the value is the same
   * tuple, so SDK helpers can pass it straight to `CalendarLookup.find`.
   */
  get acid(): [AuthId, CalendarId] {
    return [this.inner.authId, this.inner.calendarId];
  }

  /**
   * First usable conference/meeting entry point on the event, if any.
   * Reads the opaque `conferenceData` blob carried on the event; returns
   * null when absent or malformed.
   */
  get firstMeetingLink(): Option<{
    entryPointType: string;
    label: string;
    uri: string;
  }> {
    if (!this.inner.conferenceData) return null;
    const cd = this.inner.conferenceData as Option<ConferenceData>;
    const entryPoints = cd?.entryPoints;
    if (!entryPoints) return null;
    const first = entryPoints[0];
    if (!first) return null;
    if (first.entryPointType == null || typeof first.entryPointType !== "string") {
      return null;
    }
    if (first.label == null || typeof first.label !== "string") return null;
    if (first.uri == null || typeof first.uri !== "string") return null;
    return {
      entryPointType: first.entryPointType,
      label: first.label,
      uri: first.uri,
    };
  }

  title(placeholder: string = "Untitled"): string {
    return this.inner.title ?? placeholder;
  }

  get titleOpt(): Option<string> {
    return this.inner.title ?? null;
  }

  get category(): Option<string> {
    return EventItemBase.#resolveCategory(
      this.inner.category ?? null,
      this.inner.suggestedCategory ?? null,
      this.inner.useSuggestedCategory ?? null,
    );
  }

  static #resolveUseSuggestedCategory(
    category: Option<string>,
    suggestedCategory: Option<string>,
    useSuggestedCategory: Option<boolean>,
  ): boolean {
    if (useSuggestedCategory != null) return useSuggestedCategory;
    return category == null && suggestedCategory != null;
  }

  static #resolveCategory(
    category: Option<string>,
    suggestedCategory: Option<string>,
    useSuggestedCategory: Option<boolean>,
  ): Option<string> {
    const useSuggested = EventItemBase.#resolveUseSuggestedCategory(
      category,
      suggestedCategory,
      useSuggestedCategory,
    );
    return useSuggested ? suggestedCategory ?? null : category ?? null;
  }

  attendees(): Option<readonly Attendee[]> {
    return this.inner.attendees ?? null;
  }

  getAttendee(email: string = this.acid[1]): Option<Attendee> {
    return this.attendees()?.find((a) => a.email === email) ?? null;
  }

  /**
   * Virtual instances (client-generated from an RRULE) carry a
   * `RecurrenceInfo.Instance`; they need to be materialized
   * upstream before per-instance mutations persist.
   */
  needsHydration(): boolean {
    return this.recurrenceInfo instanceof RecurrenceInfo.Instance;
  }

  // ── Update helpers (return new EventItem; don't mutate) ──────────

  updateTitle(title: Option<string>): EventItem {
    return EventItem.make(
      { ...this.inner, title: title ?? this.inner.title },
      this.recurrenceInfo,
      this.origin,
    );
  }

  updateDescription(description: Option<string>): EventItem {
    return EventItem.make(
      withField(this.inner, "description", description),
      this.recurrenceInfo,
      this.origin,
    );
  }

  updateLocation(location: Option<string>): EventItem {
    return EventItem.make(
      withField(this.inner, "location", location),
      this.recurrenceInfo,
      this.origin,
    );
  }

  updateCategory(category: Option<string>): EventItem {
    return EventItem.make(
      withField(this.inner, "category", category),
      this.recurrenceInfo,
      this.origin,
    );
  }

  updateSuggestedCategory(suggestedCategory: Option<string>): EventItem {
    return EventItem.make(
      withField(this.inner, "suggestedCategory", suggestedCategory),
      this.recurrenceInfo,
      this.origin,
    );
  }

  updateUseSuggestedCategory(useSuggestedCategory: Option<boolean>): EventItem {
    return EventItem.make(
      withField(this.inner, "useSuggestedCategory", useSuggestedCategory),
      this.recurrenceInfo,
      this.origin,
    );
  }

  updateRecurrence(recurrence: Option<readonly string[]>): EventItem {
    const newRecurrenceInfo = recurrence != null
      ? new RecurrenceInfo.Parent(this.inner.id)
      : null;
    return EventItem.make(
      withField(this.inner, "recurrence", recurrence),
      newRecurrenceInfo,
      this.origin,
    );
  }

  updateTimezone(timezone: Option<string>): EventItem {
    // Clearing timezone also clears `suggestedTimezone` so the
    // event doesn't misclassify as a flight-shaped entry.
    const suggestedTimezone = timezone == null
      ? null
      : this.inner.suggestedTimezone ?? null;
    return EventItem.make(
      withField(withField(this.inner, "timezone", timezone), "suggestedTimezone", suggestedTimezone),
      this.recurrenceInfo,
      this.origin,
    );
  }

  updateResponseStatus(status: Option<string>): EventItem {
    const attendees = this.inner.attendees ?? null;
    if (!attendees) return sealedItem(this);
    const updated = attendees.map((a) =>
      a.self || a.email === this.acid[1] ? withResponseStatus(a, status) : a
    );
    return EventItem.make(
      { ...this.inner, attendees: updated },
      this.recurrenceInfo,
      this.origin,
    );
  }

  /** The event inviting `emails` (an invitee already there keeps its
   *  answer); `null` removes every attendee, as the server's clear does. */
  updateInviteeEmails(emails: Option<readonly string[]>): EventItem {
    if (emails == null) {
      return EventItem.make(
        withField(this.inner, "attendees", null),
        this.recurrenceInfo,
        this.origin,
      );
    }
    const emailSet = new Set(emails);
    const existing = this.inner.attendees ?? [];
    const byEmail = new Map<string, Attendee>(
      existing.map((a) => [a.email, a]),
    );
    const next: Attendee[] = [];
    for (const email of emailSet) {
      const prev = byEmail.get(email);
      next.push(
        prev ?? { email, displayName: null, responseStatus: null, self: null },
      );
    }
    return EventItem.make(
      { ...this.inner, attendees: next },
      this.recurrenceInfo,
      this.origin,
    );
  }

  updateTime(time: DateTime.Range<Utc>): EventItem {
    return EventItem.make(
      { ...this.inner, start: time.start, end: time.end },
      this.recurrenceInfo,
      this.origin,
    );
  }

  updateDateRange(startDate: NaiveDate, endDate: NaiveDate): EventItem {
    return EventItem.make(
      { ...this.inner, startDate, endDate },
      this.recurrenceInfo,
      this.origin,
    );
  }
}

// ── Factory + roundtrip ────────────────────────────────────────────

/** An event as a sheet holds it: timed or all-day. */
export namespace EventItem {
  /**
   * `inner` as an item with the recurrence info its own fields imply
   * (`RecurrenceInfo.ofEvent(inner)`); `make` takes the info explicitly.
   */
  export function of(inner: CalendarEvent, origin: EventOrigin): EventItem {
    return make(inner, RecurrenceInfo.ofEvent(inner), origin);
  }

  export function make(
    inner: CalendarEvent,
    recurrenceInfo: Option<RecurrenceInfo>,
    origin: EventOrigin,
  ): EventItem {
    if (inner.startDate != null && inner.endDate != null) {
      return new EventItemAllDay(inner, recurrenceInfo, origin);
    }
    return new EventItemTimed(inner, undefined, recurrenceInfo, origin);
  }

  /** Bucket a mixed list into partial-day / full-day hydrated items. */
  export function fromEvents(
    events: readonly CalendarEvent[],
    origin: EventOrigin,
  ): { timed: EventItemTimed[]; allDay: EventItemAllDay[] } {
    const partial: EventItemTimed[] = [];
    const full: EventItemAllDay[] = [];
    for (const e of events) {
      if (e.startDate != null && e.endDate != null) {
        full.push(new EventItemAllDay(e, RecurrenceInfo.ofEvent(e), origin));
      } else {
        partial.push(new EventItemTimed(e, undefined, RecurrenceInfo.ofEvent(e), origin));
      }
    }
    return { timed: partial, allDay: full };
  }
}

/**
 * An event's time extent as partial (timed) and full-day ranges, which the event renderers lay
 * events out by.
 */
export namespace EventTimeRange {
  export class Timed {
    readonly mse: NumRangeLike;

    constructor(readonly time: DateTime.Range<Utc>) {
      this.mse = {
        start: this.time.start.mse,
        end: this.time.end.mse,
      };
    }

    inThePast(now: DateTime<Utc>): boolean {
      return this.mse.end < now.mse;
    }

    /**
     * Whether the event belongs to the time-of-day window `timeRange` on the
     * day `df` (the calendar-list day filter). Half-open
     * (`TimeMembership`): an event ending exactly at the window's start —
     * a day's midnight — does not belong to it, and a zero-length event
     * belongs to the window containing its instant.
     */
    isBetween(timeRange: TimeOfDay.Range, df: DateFragment): boolean {
      const dur = timeRange.duration;
      const start = df.start.withTime(timeRange.start);
      const r = new DateTime.Range(
        start,
        start.add({ ms: dur.toMs }),
      );
      return TimeMembership.belongs(this.time, r);
    }

    duration(): Duration.Time {
      return this.time.duration;
    }
  }
}

// ── Partial-day ────────────────────────────────────────────────────

/** A timed event, spanning a start and end instant. */
export class EventItemTimed extends EventItemBase {
  constructor(
    inner: CalendarEvent,
    readonly time: EventTimeRange.Timed = new EventTimeRange.Timed(
      new DateTime.Range(inner.start, inner.end),
    ),
    /** `RecurrenceInfo.ofEvent(inner)` for the info the event implies; `null` for none. */
    recurrenceInfo: Option<RecurrenceInfo>,
    origin: EventOrigin,
  ) {
    super(inner, recurrenceInfo, origin);
  }

  startIso(): IsoDate {
    const dt = this.time.time.start.toUtc();
    return new IsoDate(dt.date, dt.time, "Z");
  }

  /** UTC time range (same object the event stores). */
  getUtcTimeRange(_tz: TimezoneRegion): DateTime.Range<"utc"> {
    return this.time.time;
  }

  /** Human-readable debug dump for event-details surfaces. */
  getTimeDebugString(): string {
    return [
      this.id,
      this.parentId,
      this.time.time.start.toString(),
      this.time.time.end.toString(),
    ].join("\n");
  }

  /** Tz-aware time range wrapper — the UI consumes this. */
  getTimeRange(tz: TimezoneRegion): DateTimeRegion.Range {
    return DateTimeRegion.Range.fromSingleTz(this.time.time, tz);
  }

  /**
   * Produce a synthetic instance at `time`, pointing back at the
   * same parent via `RecurrenceInfo.Instance`. Used by
   * EventsIndex.expandRecurrenceUntil.
   */
  makeInstance(time: DateTime.Range<Utc>): EventItemTimed {
    return new EventItemTimed(
      this.inner,
      new EventTimeRange.Timed(time),
      new RecurrenceInfo.Instance(this.parentId, time.start),
      this.origin,
    );
  }

  get range(): NumRangeLike {
    return this.time.mse;
  }

  /**
   * Merge a parent's updated fields into this instance, preserving
   * instance-specific time + recurrenceInfo. Used when the parent
   * event is patched and recurring instances need to adopt its new
   * metadata.
   */
  withParentData(parent: CalendarEvent): EventItemTimed {
    const ev: CalendarEvent = {
      ...parent,
      start: this.time.time.start,
      end: this.time.time.end,
    };
    return new EventItemTimed(ev, this.time, this.recurrenceInfo, this.origin);
  }

  /**
   * Parse the master event's RRULE into a `GoogleEventGenerator`, composed
   * as `recurrenceCompositionOf` says for this event.
   * Returns null if the event has no RRULE or if parsing fails.
   */
  async makeRecurrenceGenerator(): Promise<Option<GoogleEventGenerator>> {
    if (!this.inner.recurrence) return null;

    // Recurrence.parse expects a mutable string[]; we hold readonly.
    const recurrence = await Recurrence.parse([...this.inner.recurrence]);
    const err = recurrence.asErr();
    if (err) {
      // A TZID read before the failing line was read in UTC: traced (`tz/zone-fallback`), never left pending.
      ZoneLoad.report();
      Tracing.log("event-item/recurrence-parse-failed", {
        eventId: this.inner.id,
        error: String(err),
      });
      return null;
    }

    // An event zone that does not load is shown in UTC, traced (`tz/zone-fallback`), never silently.
    const timezone = this.inner.startTimeZone
      ? await ZoneLoad.orElse(this.inner.startTimeZone, Tzname.UTC, "event")
      : null;
    const startWall = (timezone ?? TimezoneRegion.UTC).toTz(
      this.inner.start.toUtc(),
    );

    const ok = nonnull(
      recurrence.asOk(),
      "EventItem.makeRecurrenceGenerator: a Result without an error carries a value",
    );
    const generator = await ok.generateEvents(startWall, timezone, recurrenceCompositionOf(this.inner));
    // An iCal TZID that did not load was read in UTC by chrono: traced (`tz/zone-fallback`), never silently.
    ZoneLoad.report();
    return generator;
  }
}

/**
 * The recurrence composition a master's series is generated with. This is
 * the one place a source could choose differently. Today every master,
 * synced (Google) or native or of unknown source, uses `GOOGLE`
 * (operator ruling 2026-10-03: "Google for everything"). An unsynchronised
 * DTSTART is therefore an instance on top of COUNT, as Google Calendar shows
 * it (see `RecurrenceComposition.dtstartAlwaysInstance`).
 */
export function recurrenceCompositionOf(_event: CalendarEvent): RecurrenceComposition {
  return RecurrenceComposition.GOOGLE;
}

/** Values that go with `EventItemTimed`: `compare`. */
export namespace EventItemTimed {
  export function compare(
    a: EventItemTimed,
    b: EventItemTimed,
  ): number {
    let r = a.time.mse.start - b.time.mse.start;
    if (r !== 0) return r;
    // Longer events sort first (earlier end → later in order)
    r = b.time.mse.end - a.time.mse.end;
    if (r !== 0) return r;
    return a.id.localeCompare(b.id);
  }
}

// ── Full-day ───────────────────────────────────────────────────────

/**
 * A full-day event reached `EventItemAllDay` without the `startDate` /
 * `endDate` that makes it date-ranged (the classifier routes only events
 * carrying both here, so this is a caller bug, not bad wire data).
 */
export class FullDayEventDateMissingError extends Error {
  constructor(
    readonly eventId: string,
    readonly field: "startDate" | "endDate",
  ) {
    super(
      `EventItemAllDay: event "${eventId}" has no ${field} — full-day events are date-ranged`,
    );
    this.name = "FullDayEventDateMissingError";
  }
}

/** The full-day event's `field` date; throws `FullDayEventDateMissingError` if absent. */
function fullDayDate(inner: CalendarEvent, field: "startDate" | "endDate"): NaiveDate {
  const date = inner[field];
  if (date == null) throw new FullDayEventDateMissingError(inner.id, field);
  return date;
}

/** An all-day event, spanning whole dates. */
export class EventItemAllDay extends EventItemBase {
  readonly range: NumRangeLike;
  readonly time: NaiveDate.Range;

  constructor(
    inner: CalendarEvent,
    /** `RecurrenceInfo.ofEvent(inner)` for the info the event implies; `null` for none. */
    recurrenceInfo: Option<RecurrenceInfo>,
    origin: EventOrigin,
  ) {
    super(inner, recurrenceInfo, origin);
    const startDate = fullDayDate(inner, "startDate");
    // endDate is exclusive in the wire form; subtract 1 for inclusive end.
    // 0-duration events (endDate == startDate) span exactly 1 day.
    const rawEnd = fullDayDate(inner, "endDate").addDays(-1);
    const endDate = rawEnd.dse >= startDate.dse ? rawEnd : startDate;
    this.time = new NaiveDate.Range(startDate, endDate);
    this.range = {
      start: this.time.start.dse,
      end: this.time.end.dse,
    };
  }

  startIso(): IsoDate {
    return new IsoDate(this.time.start, NaiveTime.ZERO, "Z");
  }

  /**
   * UTC time range for this full-day event. Converts the wall-clock
   * NaiveDate range to UTC using the provided timezone; end date
   * is exclusive (adds 1 day).
   */
  getUtcTimeRange(tz: TimezoneRegion): DateTime.Range<"utc"> {
    const startUtc = tz.toWallClock(this.time.start.withTime()).toUtc();
    const endUtc = tz.toWallClock(this.time.end.addDays(1).withTime()).toUtc();
    return new DateTime.Range(startUtc, endUtc);
  }

  getTimeDebugString(): string {
    return [
      this.id,
      this.time.start.toString(),
      this.time.end.toString(),
    ].join("\n");
  }

  getTimeRange(tz: TimezoneRegion): DateTimeRegion.Range {
    const start = tz.toWallClock(this.time.start.withTime()).toUtc();
    const end = tz
      .toWallClock(this.time.end.addDays(1).withTime())
      .toUtc();
    return DateTimeRegion.Range.fromSingleTz(
      new DateTime.Range(start, end),
      tz,
    );
  }
}

export type EventItem = EventItemTimed | EventItemAllDay;

/**
 * `item` as the sealed `EventItem` union — the base's update methods start
 * from it, since the base class cannot name its own variants. Every
 * constructed item is one of the two variants.
 */
function sealedItem(item: EventItemBase): EventItem {
  if (item instanceof EventItemTimed || item instanceof EventItemAllDay) return item;
  throw new TypeError("EventItem: an item is timed or all-day");
}

/**
 * Lightweight reference identifying an event item within a sheet: its
 * opaque `EventOrigin` (where it came from) plus the provider-side
 * `itemId`. Consumed by gesture actions and other UI surfaces that
 * need to dispatch writes by source identity without holding a full
 * `EventItem` handle.
 */
export interface EventRef {
  readonly origin: EventOrigin;
  readonly itemId: string;
}
